import createCachedSelector from 're-reselect';
import { RootState } from 'src/store';
import { getDocumentById, getSceneDefiningBackgroundId } from 'src/selectors/layers';
import getDecorationSpecs from './getDecorationSpecs';
import getSurfaceByPage from './getSurfaceByPage';

export type GarmentWarpAvailabilityType = { available: true } | { available: false; reason: string };

const getSurfaceForDocument = (state: RootState, id: string) => {
    const document = getDocumentById(state, id);
    return document?.page ? getSurfaceByPage(state, document.page) : undefined;
};

/**
 * Whether `GARMENT_WARP` auto-placement can be entered/recomputed for a given document.
 * Checks every input required by `computeAndApplyGarmentPlacement` (`documents/saga.ts`),
 * allowing the warp-grid option and "Recompute Placement" button to be disabled with a
 * specific explanatory tooltip rather than silently failing.
 *
 * Calibrated markers are not required up front: if none are set on the background yet,
 * default starting markers are used, allowing immediate placement that can be refined later.
 */
const getGarmentWarpAvailability = createCachedSelector(
    [getSceneDefiningBackgroundId, getDecorationSpecs, getSurfaceForDocument],
    (backgroundId, { decorationPlacement, bodyWidthMm, bodyLengthMm }, surface): GarmentWarpAvailabilityType => {
        if (!backgroundId) {
            return { available: false, reason: 'No background has been uploaded yet.' };
        }

        if (!surface?.name) {
            return { available: false, reason: "This document isn't on a recognized surface/page." };
        }

        const effectivePanelName = surface.panelName ?? surface.name;
        const panel = decorationPlacement?.find((entry) => entry.panelName === effectivePanelName);
        if (!panel) {
            return { available: false, reason: `No decoration-placement data for "${surface.name}".` };
        }

        // Checked against the resolved mm values directly (not sizeChart.tables.length) —
        // computeAndApplyGarmentPlacement needs one of these specific numbers, not merely "a
        // size chart table exists for this product" (which reflects `buildSizeChartFromProduct`'s
        // less robust first-name-match-only resolution, and could theoretically be non-empty while
        // still missing the value the current scaleAxis actually needs — see
        // productPropertySizeChart.ts's resolveBodyWidthMm/resolveBodyLengthMm docstrings).
        const hasWidth = bodyWidthMm !== undefined;
        const hasLength = bodyLengthMm !== undefined;
        if (!hasWidth && !hasLength) {
            return { available: false, reason: 'No size chart data is available for this product.' };
        }

        return { available: true };
    },
)((_state, id) => id);

export default getGarmentWarpAvailability;
